import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { useTurnstile, getTurnstileSiteKey } from '@/lib/turnstile';

/*
 * TurnstileWidget (Sessio 7a; reset-hotfix Sessio 7b).
 *
 * Renderoi Cloudflare Turnstile -widgetin lomakkeen sisaan ja kutsuu onToken-
 * callbackin kun token on saatavilla. Widget on yleensa nakymaton ("managed"
 * mode) ja vaatii vain kayttajan klikkaamaan checkboxin jos Cloudflare epailee
 * bottia.
 *
 * **resetSignal-prop**: Cloudflare Turnstile -tokeni on KERTAKAYTTOINEN. Kun
 * token on lahetetty backendiin (joka kutsuu siteverify-API:n), sama token ei
 * kelpaa toiseen pyyntoon. Lomakkeen pitaa siksi resetoida widget jokaisen
 * epaonnistuneen submitin jalkeen. Anna resetSignal:lle muuttuva arvo (esim.
 * mutation.failureCount), jolloin widget kutsuu turnstile.reset():n ja saa
 * uuden tokenin.
 *
 * Dev-bypass: jos VITE_TURNSTILE_SITE_KEY puuttuu env:sta, hook palauttaa
 * "dev-bypass"-tokenin valittomasti eika nayta widgetia. Backend hyvaksyy taman
 * dev-tilassa.
 */

type Props = {
  onToken: (token: string) => void;
  onError?: (errorCode: string) => void;
  className?: string;
  /**
   * Kun arvo muuttuu, widget resetoidaan ja sen jalkeen uusi token annetaan
   * onToken-callbackin kautta. Tyypillisesti `mutation.failureCount`.
   */
  resetSignal?: number;
};

export function TurnstileWidget({ onToken, onError, className, resetSignal }: Props) {
  const { t } = useTranslation();
  const siteKey = getTurnstileSiteKey();
  const { containerRef, bypassed, reset, error, retry } = useTurnstile({ siteKey, onToken, onError });

  // Reset widget kun resetSignal muuttuu (esim. submit epaonnistui).
  // Cloudflare Turnstile -tokeni on kertakayttoinen, joten reset() on pakollinen
  // jotta uusi yritys saa uuden tokenin.
  //
  // Lisaksi tyhjennetaan parent-lomakkeen token kutsumalla onToken('') heti.
  // Tama estaa Submit-painikkeen klikkauksen vanhalla tokenilla ennen kuin
  // widget on antanut uuden. Painike on `disabled={!turnstileToken}`, joten
  // tyhja arvo disabloi sen kunnes uusi token saadaan callbackin kautta.
  //
  // Dev-bypass-tilassa token pysyy 'dev-bypass'-merkkijonona, ei resetoita.
  useEffect(() => {
    if (resetSignal !== undefined && resetSignal > 0 && !bypassed) {
      onToken('');
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  if (bypassed) {
    return (
      <p className="text-xs text-usva-t" data-testid="turnstile-bypassed">
        {t('auth.turnstile.devBypass')}
      </p>
    );
  }

  // Kontti pysyy aina DOMissa: widget renderoidaan siihen myos uudelleenyrityksessa.
  return (
    <div className={className} aria-label="Bottivarmennus (Cloudflare Turnstile)">
      <div className="flex flex-col items-center gap-2">
        <div ref={containerRef} data-testid="turnstile-widget" />
        {error && <TurnstileError code={error} onRetry={retry} />}
      </div>
    </div>
  );
}

function TurnstileError({ code, onRetry }: { code: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      data-testid="turnstile-error"
      className="flex max-w-sm flex-col items-center gap-2 text-center"
    >
      <p className="text-sm text-oranssi">{t('auth.turnstile.error')}</p>
      <p className="text-xs text-usva-t">{t('auth.turnstile.errorCode', { code })}</p>
      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
        {t('auth.turnstile.retry')}
      </Button>
    </div>
  );
}
